import React, { useCallback, useEffect, useState, memo } from "react";
import { Text, View, XStack } from "tamagui";

import { brandShadowSm, customColorTokens } from "../../../tamagui.config";
import { Header } from "../../components/design-system/Header";

import { useAtomValue } from "jotai";
import { currentUserAtom } from "../../../atoms/global";

import { NavigationProp, useNavigation } from "@react-navigation/native";
import { RidesNavigatorParamList } from "../../navigators/RidesNavigator";

import { PlusIcon } from "react-native-heroicons/solid";

import { Button } from "../../components/design-system";
import { ConfirmationModal } from "../../components/design-system/ConfirmationModal";
import { useStravaAuth } from "../../features/Oauth";
import { useCreateAuthToken } from "../../features/Oauth/hooks/useCreateAuthToken";
import { AuthSessionResult } from "expo-auth-session";
import { MyCalendar } from "../MyCalendar";
import { MyInvites } from "../MyCalendar/components/MyInvites/MyInvites";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import { TouchableOpacity } from "react-native";
import { useApiQuery, useApiQueryClient } from "../../../lib/api";
import { Loader } from "../../components/design-system/Loader";

const Tab = createMaterialTopTabNavigator();

type StravaTokenResponse = {
  strava_token: {
    id: string;
    code: string;
    token: string;
    expires_at: string | null;
  } | null;
};

const TabBarComponent = memo((props: Record<string, unknown>) => {
  const state = props.state as { routes: { name: string; key: string; params: unknown }[]; index: number };
  const nav = props.navigation as { emit: (args: { type: string; target: string; canPreventDefault: boolean }) => { defaultPrevented: boolean }; navigate: (name: string, params: unknown) => void };

  return (
    <XStack
      w="100%"
      bg="$brandParchment"
      gap={4}
      justifyContent="center"
      p={4}
      px="$4"
      pt="$2"
    >
      {state.routes.map((route, index: number) => {
        const label = route.name;
        const isFocused = state.index === index;

        const onPress = () => {
          const event = nav.emit({
            type: "tabPress",
            target: route.key,
            canPreventDefault: true,
          });

          if (!isFocused && !event.defaultPrevented) {
            nav.navigate(route.name, route.params);
          }
        };

        return (
          <TouchableOpacity
            onPress={onPress}
            key={route.key}
            style={{
              flex: 1,
              backgroundColor: isFocused ? "white" : "transparent",
              borderColor: isFocused ? "black" : "transparent",
              borderWidth: 1,
              borderRadius: 12,
              paddingVertical: 8,
              paddingHorizontal: 12,
              alignItems: "center",
              justifyContent: "center",
              ...(isFocused
                ? {
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 4,
                    elevation: 3,
                  }
                : {}),
            }}
            activeOpacity={0.7}
          >
            <Text fontSize={16} fontFamily="$heading">
              {label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </XStack>
  );
});

export const CommunityRides = () => {
  const currentUser = useAtomValue(currentUserAtom);
  const queryClient = useApiQueryClient();

  const { data, isLoading } = useApiQuery<StravaTokenResponse>(
    ["strava-token"],
    "/api/v1/auth/strava",
    { enabled: !!currentUser?.id }
  );

  const refreshQuery = () => {
    queryClient.invalidateQueries({ queryKey: ["strava-token"] });
  };

  if (isLoading) return <Loader />;

  return (
    <CommunityRidesInner
      authToken={data?.strava_token ?? null}
      refreshQuery={refreshQuery}
    />
  );
};

const CommunityRidesInner = memo(
  ({
    authToken,
    refreshQuery,
  }: {
    authToken: StravaTokenResponse["strava_token"];
    refreshQuery: () => void;
  }) => {
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

    const navigation = useNavigation<NavigationProp<RidesNavigatorParamList>>();

    const { response, promptAsync, getToken } = useStravaAuth();
    const user = useAtomValue(currentUserAtom);

    const [handleCreateAuthToken] = useCreateAuthToken(() => {
      handleGetToken();
      refreshQuery();
    });

    const handleStravaResponse = useCallback(
      (res: AuthSessionResult, userId: string) => {
        if (res.type === "success") {
          const { code } = res.params;
          handleCreateAuthToken({
            code,
            provider: "Strava",
            profile_id: userId,
          });
        }
      },
      []
    );

    useEffect(() => {
      if (response && response.type === "success") {
        handleStravaResponse(response, user?.id ?? "");
      }
    }, [response, handleStravaResponse]);

    const handleGetToken = async () => {
      await getToken();
    };

    const handleHostPress = () => {
      if (authToken) {
        navigation.navigate("CreateRide");
      } else {
        setIsAuthModalOpen(true);
      }
    };

    const startStravaSync = () => {
      setIsAuthModalOpen(false);
      promptAsync();
    };

    return (
      <View
        flex={1}
        style={{ flex: 1, backgroundColor: customColorTokens.brandParchment }}
      >
        <Header
          title={"Rides"}
          headerRight={
            <XStack alignItems="center" gap={4}>
              <Button
                onPress={handleHostPress}
                py={4}
                {...brandShadowSm}
                h={32}
              >
                <XStack alignItems="center">
                  <Text fontFamily="$heading">HOST</Text>
                  <PlusIcon width={16} height={16} color="black" />
                </XStack>
              </Button>
            </XStack>
          }
        />

        <Tab.Navigator
          screenOptions={{
            lazy: false,
            swipeEnabled: false,
            tabBarStyle: { display: "none" },
          }}
          tabBar={(props) => <TabBarComponent {...props} />}
        >
          <Tab.Screen name="Your Rides" component={MyCalendar} />
          <Tab.Screen name="Invites" component={MyInvites} />
        </Tab.Navigator>

        <ConfirmationModal
          isOpen={isAuthModalOpen}
          title="Connect Strava"
          subtitle="To host a ride, you need to connect your Strava account."
          yesMessage="Connect Strava"
          noMessage="No, I don't want to host"
          onConfirm={() => startStravaSync()}
          onCancel={() => setIsAuthModalOpen(false)}
        />
      </View>
    );
  }
);
